import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { SpriteIcon } from '@/components/SpriteIcon';
import type { MfaChallengeResponse } from '@/api/types';
import { AuthCard, AuthErrorSummary } from './AuthCard';
import { AuthField } from './AuthField';
import { SubmitButton } from './SubmitButton';
import {
  mfaCodeSchema,
  recoveryCodeSchema,
  type MfaCodeValues,
  type RecoveryCodeValues,
} from './schemas';
import { useServerValidation } from './useServerValidation';

/**
 * The second step of a sign-in, reached when `/api/auth/login` answers
 * `requiresTwoFactor`.
 *
 * Anonymous by necessity: the password was accepted but no application cookie
 * exists yet, so the caller is identified only by Identity's two-factor cookie.
 * That cookie also carries the deadline — when it expires the server reports
 * `pending: false` and the user starts again at the login screen.
 */
export function MfaChallengePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state as { from?: string } | null;
  const from = state?.from ?? '/';

  const [loading, setLoading] = useState(true);
  const [usingRecoveryCode, setUsingRecoveryCode] = useState(false);

  // Load the challenge once. The ref guards against StrictMode's double effect.
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;

    void (async () => {
      try {
        const result = await api.get<MfaChallengeResponse>('/api/auth/mfa/challenge');

        if (!result.pending) {
          // Nothing in flight: an expired window, or a direct visit to this URL.
          navigate('/auth-login', { replace: true });
          return;
        }

        setLoading(false);
      } catch {
        navigate('/auth-login', { replace: true });
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <AuthCard centered>
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        {t('Loading', 'Loading...')}
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={t('auth:Auth.MFA.ChallengeTitle', 'Two-step verification')}
      subtitle={
        usingRecoveryCode
          ? t(
              'auth:Auth.MFA.RecoverySubtitle',
              'Enter one of the recovery codes you saved when you set this up.',
            )
          : t(
              'auth:Auth.MFA.ChallengeSubtitle',
              'Enter the 6-digit code from your authenticator app.',
            )
      }
    >
      {/*
        Remounted on the toggle via `key`, which resets the resolver, the field
        registration and any error left over from the other form. Keeping one
        form and swapping the schema underneath leaves react-hook-form validating
        a field that no longer exists.
      */}
      {usingRecoveryCode ? (
        <RecoveryCodeForm key="recovery" from={from} onUseAuthenticator={() => setUsingRecoveryCode(false)} />
      ) : (
        <AuthenticatorForm key="authenticator" from={from} onUseRecoveryCode={() => setUsingRecoveryCode(true)} />
      )}
    </AuthCard>
  );
}

/**
 * Shared tail of both submit handlers: everything that happens once the server
 * has ruled on the code, which is identical whichever kind it was.
 */
function useChallengeOutcome(from: string) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (result: {
    succeeded: boolean;
    isLockedOut: boolean;
    challengeExpired: boolean;
    mustChangePassword: boolean;
    recoveryCodesExhausted: boolean;
    error: string | null;
  }): string | null => {
    if (result.succeeded) {
      if (result.mustChangePassword) {
        navigate('/auth-changepassword', { replace: true });
        return null;
      }

      // Spending the last code leaves a lost authenticator with no way back, so
      // send them somewhere they can do something about it rather than dropping
      // them on the dashboard with a toast they will dismiss.
      navigate(result.recoveryCodesExhausted ? '/user-profile' : from, {
        replace: true,
        state: result.recoveryCodesExhausted
          ? {
              status: t(
                'auth:Auth.MFA.LastRecoveryCodeUsed',
                'That was your last recovery code. Generate a new set now, or you risk losing access to your account.',
              ),
            }
          : undefined,
      });
      return null;
    }

    if (result.isLockedOut) {
      navigate('/auth-lockout', { replace: true });
      return null;
    }

    if (result.challengeExpired) {
      navigate('/auth-login', {
        replace: true,
        state: {
          status: t(
            'auth:Auth.MFA.ChallengeExpired',
            'Your sign-in request timed out. Please sign in again.',
          ),
        },
      });
      return null;
    }

    return (
      result.error ?? t('auth:Auth.MFA.InvalidCode', 'That code is not valid. Please try again.')
    );
  };
}

function AuthenticatorForm({ from, onUseRecoveryCode }: { from: string; onUseRecoveryCode: () => void }) {
  const { t } = useTranslation();
  const { verifyMfa } = useAuth();
  const handleOutcome = useChallengeOutcome(from);

  const schema = useMemo(() => mfaCodeSchema(t), [t]);
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MfaCodeValues>({ resolver: zodResolver(schema), defaultValues: { code: '' } });

  const { formErrors, clearFormErrors, showFormError, applyServerErrors } = useServerValidation(
    setError,
    ['code'] as const,
  );

  const onSubmit = handleSubmit(async (values) => {
    clearFormErrors();

    try {
      const message = handleOutcome(await verifyMfa({ code: values.code }));
      if (message) {
        reset({ code: '' });
        showFormError(message);
      }
    } catch (error) {
      applyServerErrors(error);
    }
  });

  return (
    <>
      <AuthErrorSummary errors={formErrors} />

      <form onSubmit={onSubmit} noValidate>
        <AuthField
          {...register('code')}
          label={t('auth:Auth.MFA.CodeLabel', 'Verification code')}
          large
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder={t('auth:Auth.MFA.CodePlaceholder', '123456')}
          error={errors.code?.message}
        />

        <div className="d-grid gap-2">
          <SubmitButton
            icon="check"
            label={t('auth:Auth.MFA.Verify', 'Verify')}
            pending={isSubmitting}
          />
        </div>
      </form>

      <div className="text-center mt-3">
        <button
          type="button"
          className="btn btn-link btn-sm text-decoration-none d-inline-flex align-items-center"
          onClick={onUseRecoveryCode}
        >
          <SpriteIcon name="key" size={14} className="sa-icon me-1 flex-shrink-0" />
          {t('auth:Auth.MFA.UseRecoveryCode', 'Use a recovery code instead')}
        </button>
      </div>
    </>
  );
}

function RecoveryCodeForm({ from, onUseAuthenticator }: { from: string; onUseAuthenticator: () => void }) {
  const { t } = useTranslation();
  const { verifyMfa } = useAuth();
  const handleOutcome = useChallengeOutcome(from);

  const schema = useMemo(() => recoveryCodeSchema(t), [t]);
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RecoveryCodeValues>({
    resolver: zodResolver(schema),
    defaultValues: { recoveryCode: '' },
  });

  const { formErrors, clearFormErrors, showFormError, applyServerErrors } = useServerValidation(
    setError,
    ['recoveryCode'] as const,
  );

  const onSubmit = handleSubmit(async (values) => {
    clearFormErrors();

    try {
      const message = handleOutcome(await verifyMfa({ recoveryCode: values.recoveryCode }));
      if (message) {
        reset({ recoveryCode: '' });
        showFormError(message);
      }
    } catch (error) {
      applyServerErrors(error);
    }
  });

  return (
    <>
      <AuthErrorSummary errors={formErrors} />

      <form onSubmit={onSubmit} noValidate>
        <AuthField
          {...register('recoveryCode')}
          label={t('auth:Auth.MFA.RecoveryCodeLabel', 'Recovery code')}
          large
          autoFocus
          autoComplete="one-time-code"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={13}
          placeholder="XXXXX-XXXXX"
          error={errors.recoveryCode?.message}
        />

        <p className="text-muted small">
          {t('auth:Auth.MFA.RecoveryCodeHint', 'Each code works only once.')}
        </p>

        <div className="d-grid gap-2">
          <SubmitButton
            icon="check"
            label={t('auth:Auth.MFA.Verify', 'Verify')}
            pending={isSubmitting}
          />
        </div>
      </form>

      <div className="text-center mt-3">
        <button
          type="button"
          className="btn btn-link btn-sm text-decoration-none d-inline-flex align-items-center"
          onClick={onUseAuthenticator}
        >
          <SpriteIcon name="smartphone" size={14} className="sa-icon me-1 flex-shrink-0" />
          {t('auth:Auth.MFA.UseAuthenticator', 'Use my authenticator app instead')}
        </button>
      </div>
    </>
  );
}
